import type { HighlightPart } from "shared/types";

// Same folding as the backend search (Str::ascii + case-insensitive):
// strip diacritics and lowercase, so "Jürgen" matches "jurgen".
export const foldSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// One-line search-result excerpt: strips HTML, then windows ±radius characters
// around the first folded match so buildHighlightParts can highlight it. Falls
// back to the snippet start when the needle doesn't occur in the visible text.
export const buildSearchExcerpt = (
  html: string,
  needle: string,
  radius = 60,
): string => {
  // Let the parser find the tags (a regex like /<[^>]*>/ misses nested tags);
  // it also decodes every named and numeric entity (&amp;, &#8364;, …). Every
  // tag boundary becomes a space so block boundaries keep separating words,
  // and \s+ collapses those plus the NBSP that &nbsp; decodes to.
  const { body } = new DOMParser().parseFromString(html, "text/html");
  body.querySelectorAll("*").forEach((element) => {
    element.before(" ");
    element.after(" ");
  });
  const text = (body.textContent ?? "").replace(/\s+/g, " ").trim();

  if (text === "") {
    return "";
  }

  const foldedNeedle = foldSearchText(needle.trim());
  const index =
    foldedNeedle === "" ? -1 : foldSearchText(text).indexOf(foldedNeedle);

  if (index === -1) {
    const head = text.slice(0, radius * 2);
    return head.length < text.length ? `${head}…` : head;
  }

  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + foldedNeedle.length + radius);

  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";

  return `${prefix}${text.slice(start, end)}${suffix}`;
};

export const buildHighlightParts = (
  text: string,
  needle: string,
): HighlightPart[] => {
  const foldedNeedle = foldSearchText(needle.trim());

  if (foldedNeedle === "") {
    return [{ text, isMatch: false }];
  }

  const index = foldSearchText(text).indexOf(foldedNeedle);

  if (index === -1) {
    return [{ text, isMatch: false }];
  }

  return [
    { text: text.slice(0, index), isMatch: false },
    { text: text.slice(index, index + foldedNeedle.length), isMatch: true },
    { text: text.slice(index + foldedNeedle.length), isMatch: false },
  ].filter((part) => part.text !== "");
};
